import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, Heart, X, MapPin, Sparkles, Star, Loader2, 
  MessageCircle, ChevronLeft, ChevronRight, MoreVertical,
  Shield, Flag, Video, Info, Navigation
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { useAuth, API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import BlockReportModal from '../components/BlockReportModal';
import { formatDistance } from '../hooks/useGeolocation';

const ProfileView = () => {
  const { userId } = useParams();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [compatibility, setCompatibility] = useState(null);
  const [loadingCompat, setLoadingCompat] = useState(false);
  const [swiping, setSwiping] = useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [showFullBio, setShowFullBio] = useState(false);
  const [showBlockReport, setShowBlockReport] = useState(false);
  const [isMatch, setIsMatch] = useState(false);

  const { user, token } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchProfile();
    checkIfMatch();
  }, [userId]);

  const fetchProfile = async () => {
    try {
      const res = await axios.get(`${API}/profile/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfile(res.data);
      
      // Log profile view
      await axios.post(`${API}/profile/${userId}/view`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      }).catch(() => {});
      
      // Auto-fetch quick compatibility
      fetchQuickCompatibility();
    } catch (err) {
      toast.error('Kunne ikke indlæse profil');
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  const checkIfMatch = async () => {
    try {
      const res = await axios.get(`${API}/matches`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const matched = res.data.some(m => 
        m.user?.id === userId || 
        m.user1_id === userId || 
        m.user2_id === userId
      );
      setIsMatch(matched);
    } catch (err) {
      console.error('Could not check match status');
    }
  };

  const fetchQuickCompatibility = async () => {
    try {
      const res = await axios.get(`${API}/ai/quick-compatibility/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompatibility(res.data);
    } catch (err) {
      console.error('Could not fetch compatibility');
    }
  };

  const fetchFullCompatibility = async () => {
    if (!user?.is_premium || !['gold', 'platinum'].includes(user.premium_tier)) {
      toast.error('AI Kompatibilitet er en Guld/Platin funktion');
      return;
    }
    
    setLoadingCompat(true);
    try {
      const res = await axios.get(`${API}/ai/compatibility/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompatibility({ ...compatibility, ...res.data, is_full: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunne ikke hente kompatibilitet');
    } finally {
      setLoadingCompat(false);
    }
  };

  const handleSwipe = async (action) => {
    setSwiping(true);
    try {
      const res = await axios.post(`${API}/swipe`, {
        target_user_id: userId,
        action: action
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.data.is_match) {
        toast.success("Det er et match! I kan nu chatte.", {
          action: {
            label: 'Chat',
            onClick: () => navigate(`/chat/${userId}`)
          }
        });
        navigate(`/chat/${userId}`);
      } else if (action === 'like') {
        toast.success('Liket! Kryds fingre for et match.');
        navigate('/discover');
      } else if (action === 'superlike') {
        toast.success('Super Like sendt! 💫');
        navigate('/discover');
      } else {
        navigate('/discover');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Handling fejlede');
    } finally {
      setSwiping(false);
    }
  };

  const nextPhoto = () => {
    if (profile?.photos?.length > 1) {
      setCurrentPhotoIndex((prev) => (prev + 1) % profile.photos.length);
    }
  };

  const prevPhoto = () => {
    if (profile?.photos?.length > 1) {
      setCurrentPhotoIndex((prev) => (prev - 1 + profile.photos.length) % profile.photos.length);
    }
  };

  const handleBlocked = () => {
    navigate('/discover');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  if (!profile) return null;

  const currentPhoto = profile.photos?.[currentPhotoIndex] || 'https://via.placeholder.com/400x500?text=Intet+billede';

  return (
    <div className="min-h-screen bg-background">
      {/* Photo Section - Tinder Style */}
      <div className="relative h-[70vh] max-h-[600px]">
        {/* Photo */}
        <motion.img
          key={currentPhotoIndex}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          src={currentPhoto.startsWith('/api') ? `${API.replace('/api', '')}${currentPhoto}` : currentPhoto}
          alt={profile.name}
          className="w-full h-full object-cover"
        />

        {/* Photo Navigation */}
        {profile.photos?.length > 1 && (
          <>
            {/* Photo indicators */}
            <div className="absolute top-4 left-14 right-14 flex gap-1 z-20">
              {profile.photos.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentPhotoIndex(i)}
                  className={`flex-1 h-1.5 rounded-full transition-colors ${
                    i === currentPhotoIndex ? 'bg-white' : 'bg-white/40'
                  }`}
                  aria-label={`Billede ${i + 1}`}
                />
              ))}
            </div>

            {/* Tap zones for photo navigation */}
            <button
              onClick={(e) => { e.stopPropagation(); prevPhoto(); }}
              className="absolute left-0 top-0 w-1/3 h-full z-10 cursor-pointer"
              aria-label="Forrige billede"
            />
            <button
              onClick={(e) => { e.stopPropagation(); nextPhoto(); }}
              className="absolute right-0 top-0 w-1/3 h-full z-10 cursor-pointer"
              aria-label="Næste billede"
            />
          </>
        )}

        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/30 pointer-events-none" />

        {/* Back button */}
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(-1)}
          className="absolute top-4 left-4 text-white bg-black/20 hover:bg-black/40 rounded-full z-10"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-6 h-6" />
        </Button>

        {/* More options */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="absolute top-4 right-4 text-white bg-black/20 hover:bg-black/40 rounded-full z-10"
              data-testid="more-options-btn"
            >
              <MoreVertical className="w-6 h-6" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => setShowBlockReport(true)}>
              <Shield className="w-4 h-4 mr-2" />
              Bloker
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setShowBlockReport(true)}>
              <Flag className="w-4 h-4 mr-2" />
              Rapporter
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Basic info overlay */}
        <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
          <div className="flex items-end justify-between">
            <div>
              <h1 className="text-3xl font-display font-bold flex items-center gap-2">
                {profile.name}{profile.age ? `, ${profile.age}` : ''}
                {profile.is_boosted && (
                  <Badge className="bg-accent text-white">
                    <Sparkles className="w-3 h-3 mr-1" />
                    Boosted
                  </Badge>
                )}
              </h1>
              <div className="flex items-center gap-3 text-white/80 mt-1">
                {profile.location && (
                  <div className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    <span>{profile.location}</span>
                  </div>
                )}
                {profile.distance_km !== null && profile.distance_km !== undefined && (
                  <div className="flex items-center gap-1">
                    <Navigation className="w-4 h-4" />
                    <span>{formatDistance(profile.distance_km)}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Compatibility badge */}
            {compatibility && (
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="bg-white/20 backdrop-blur-sm rounded-full px-3 py-1.5 flex items-center gap-2"
              >
                <Sparkles className="w-4 h-4 text-accent" />
                <span className="font-semibold">{compatibility.score}%</span>
              </motion.div>
            )}
          </div>
        </div>
      </div>

      {/* Profile Details */}
      <div className="p-6 pb-32 space-y-6">
        {/* Bio */}
        {profile.bio && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-card rounded-2xl border border-border p-4"
          >
            <h3 className="font-semibold text-foreground mb-2 flex items-center gap-2">
              <Info className="w-4 h-4" />
              Om {profile.name}
            </h3>
            <p className={`text-muted-foreground ${!showFullBio && profile.bio.length > 150 ? 'line-clamp-3' : ''}`}>
              {profile.bio}
            </p>
            {profile.bio.length > 150 && (
              <button
                onClick={() => setShowFullBio(!showFullBio)}
                className="text-primary text-sm mt-2"
              >
                {showFullBio ? 'Vis mindre' : 'Læs mere'}
              </button>
            )}
          </motion.div>
        )}

        {/* Profile Prompts */}
        {profile.prompts?.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="space-y-3"
          >
            {profile.prompts.map((prompt, index) => (
              <div
                key={prompt.prompt_id || index}
                className="bg-card rounded-2xl border border-border p-4"
              >
                <p className="text-sm text-muted-foreground mb-2">{prompt.question}</p>
                <p className="text-foreground font-medium">{prompt.answer}</p>
              </div>
            ))}
          </motion.div>
        )}

        {/* Compatibility Section */}
        {compatibility && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-gradient-to-br from-primary/10 to-accent/10 rounded-2xl border border-primary/20 p-4"
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-foreground flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-primary" />
                Kompatibilitet
              </h3>
              <span className="text-2xl font-bold text-primary">{compatibility.score}%</span>
            </div>

            {compatibility.shared_interests?.length > 0 && (
              <div className="mb-3">
                <p className="text-sm text-muted-foreground mb-2">Fælles interesser:</p>
                <div className="flex flex-wrap gap-2">
                  {compatibility.shared_interests.map((interest, i) => (
                    <Badge key={i} variant="secondary" className="rounded-full">
                      {interest}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {compatibility.is_full && compatibility.analysis && (
              <p className="text-sm text-muted-foreground mb-3">{compatibility.analysis}</p>
            )}

            {!compatibility.is_full && user?.is_premium && ['gold', 'platinum'].includes(user.premium_tier) && (
              <Button
                variant="outline"
                size="sm"
                onClick={fetchFullCompatibility}
                disabled={loadingCompat}
                className="rounded-full"
              >
                {loadingCompat ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Sparkles className="w-4 h-4 mr-2" />
                )}
                Se fuld AI-analyse
              </Button>
            )}
          </motion.div>
        )}

        {/* Interests */}
        {profile.interests?.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <h3 className="font-semibold text-foreground mb-3">Interesser</h3>
            <div className="flex flex-wrap gap-2">
              {profile.interests.map((interest, i) => (
                <Badge 
                  key={i} 
                  variant={compatibility?.shared_interests?.includes(interest) ? 'default' : 'secondary'}
                  className="rounded-full"
                >
                  {interest}
                </Badge>
              ))}
            </div>
          </motion.div>
        )}
      </div>

      {/* Action Buttons - Fixed bottom */}
      <div className="fixed bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-background via-background to-transparent">
        <div className="max-w-lg mx-auto">
          {isMatch ? (
            /* Already matched - show message button */
            <Button
              onClick={() => navigate(`/chat/${userId}`)}
              className="w-full h-14 rounded-full bg-primary hover:bg-primary/90 text-lg"
              data-testid="message-btn"
            >
              <MessageCircle className="w-6 h-6 mr-2" />
              Send Besked
            </Button>
          ) : (
            /* Not matched - show swipe buttons */
            <div className="flex items-center justify-center gap-4">
              <Button
                size="lg"
                variant="outline"
                className="w-16 h-16 rounded-full border-2 border-destructive hover:bg-destructive/10"
                onClick={() => handleSwipe('pass')}
                disabled={swiping}
                data-testid="pass-btn"
              >
                <X className="w-8 h-8 text-destructive" />
              </Button>

              <Button
                size="lg"
                variant="outline"
                className="w-14 h-14 rounded-full border-2 border-accent hover:bg-accent/10"
                onClick={() => handleSwipe('superlike')}
                disabled={swiping || !user?.is_premium}
                title={user?.is_premium ? 'Super Like' : 'Premium funktion'}
                data-testid="superlike-btn"
              >
                <Star className={`w-6 h-6 ${user?.is_premium ? 'text-accent' : 'text-muted-foreground'}`} />
              </Button>

              <Button
                size="lg"
                className="w-16 h-16 rounded-full bg-primary hover:bg-primary/90"
                onClick={() => handleSwipe('like')}
                disabled={swiping}
                data-testid="like-btn"
              >
                <Heart className="w-8 h-8 text-white" />
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Block/Report Modal */}
      <BlockReportModal
        isOpen={showBlockReport}
        onClose={() => setShowBlockReport(false)}
        userId={userId}
        userName={profile.name}
        onBlocked={handleBlocked}
      />
    </div>
  );
};

export default ProfileView;
